import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '../../components/MainLayout';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@werkstatt/ui';
import { Input } from '@werkstatt/ui';
import { useToast } from '../../hooks/use-toast';
import { Plus, Search, Pencil, Trash2, Calendar, X, Globe } from 'lucide-react';
import api from '../../lib/api';
import type { Booking, BookingListResponse } from '../../types/booking';
import {
  formatBookingDateRange,
  formatPrice,
  STATUS_LABELS,
  STATUS_COLORS,
  calculateBookingDays
} from '../../types/booking';
import { BookingDialog } from './BookingDialog';

export default function Bookings() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch bookings
  const { data, isLoading } = useQuery<BookingListResponse>({
    queryKey: ['bookings', page, search, onlineOnly],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', '50');
      if (search) params.append('search', search);
      if (onlineOnly) params.append('is_online_booking', 'true');

      const response = await api.get(`/bookings?${params.toString()}`);
      return response.data;
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/bookings/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      toast({
        title: 'Buchung gelöscht',
        description: 'Die Buchung wurde erfolgreich gelöscht.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Fehler',
        description:
          (isAxiosError(error) ? error.response?.data?.detail : undefined) || 'Die Buchung konnte nicht gelöscht werden.',
        variant: 'destructive',
      });
    },
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) => {
      await api.post(`/bookings/${id}/cancel`, { reason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      toast({
        title: 'Buchung storniert',
        description: 'Die Buchung wurde erfolgreich storniert.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Fehler',
        description:
          (isAxiosError(error) ? error.response?.data?.detail : undefined) || 'Die Buchung konnte nicht storniert werden.',
        variant: 'destructive',
      });
    },
  });

  const handleEdit = (booking: Booking) => {
    setEditingBooking(booking);
    setDialogOpen(true);
  };

  const handleDelete = (booking: Booking) => {
    if (
      window.confirm(
        `Möchten Sie die Buchung "${booking.booking_number}" wirklich löschen?`
      )
    ) {
      deleteMutation.mutate(booking.id);
    }
  };

  const handleCancel = (booking: Booking) => {
    const reason = window.prompt(
      'Bitte geben Sie einen Stornierungsgrund ein:',
      'Stornierung auf Kundenwunsch'
    );

    if (reason) {
      cancelMutation.mutate({ id: booking.id, reason });
    }
  };

  const getStatusBadge = (status: Booking['status']) => {
    return (
      <span
        className={`px-2 py-1 rounded-pill text-xs font-medium ${
          STATUS_COLORS[status]
        }`}
      >
        {STATUS_LABELS[status]}
      </span>
    );
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Calendar className="h-7 w-7" />
              Buchungen
            </h1>
            <p className="text-muted-foreground mt-1">
              Verwalten Sie Ihre Fahrzeugbuchungen
            </p>
          </div>
          <Button
            onClick={() => {
              setEditingBooking(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Neue Buchung
          </Button>
        </div>

        {/* Search */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle className="font-display text-lg">Suche</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Suchen nach Buchungsnummer..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="pl-10"
                />
              </div>
              <Button
                type="button"
                variant={onlineOnly ? 'default' : 'outline'}
                onClick={() => {
                  setOnlineOnly((v) => !v);
                  setPage(1);
                }}
              >
                <Globe className="mr-2 h-4 w-4" />
                Nur Online-Anfragen
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle className="font-display">Buchungen{data ? ` (${data.total})` : ''}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 px-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : !data || data.bookings.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Calendar className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Keine Buchungen gefunden</h3>
              <p className="text-muted-foreground mb-4">
                {search
                  ? 'Keine Buchungen entsprechen Ihrer Suche.'
                  : 'Fügen Sie Ihre erste Buchung hinzu.'}
              </p>
              {!search && (
                <Button
                  onClick={() => {
                    setEditingBooking(null);
                    setDialogOpen(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Erste Buchung erstellen
                </Button>
              )}
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Buchungsnummer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Zeitraum
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Kunde
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Fahrzeug
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Gesamtpreis
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.bookings.map((booking) => (
                    <tr
                      key={booking.id}
                      className="hover:bg-muted/30 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium flex items-center gap-1.5">
                          {booking.booking_number}
                          {booking.is_online_booking && (
                            <span
                              title="Online-Anfrage"
                              className="inline-flex items-center gap-1 rounded-pill
                                bg-blue-500/15 text-blue-600 dark:text-blue-400 text-[10px] font-medium px-2 py-0.5"
                            >
                              <Globe className="h-2.5 w-2.5" />
                              Online
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {calculateBookingDays(booking.start_date, booking.end_date)}{' '}
                          Tage
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          {formatBookingDateRange(booking)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          {booking.customer_name || `Kunde #${booking.customer_id}`}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          {booking.vehicle_name || `Fahrzeug #${booking.vehicle_id}`}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium">
                          {formatPrice(booking.total_price_cents)}
                        </span>
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(booking.status)}</td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(booking)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {booking.status === 'pending' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(booking)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                        {(booking.status === 'pending' ||
                          booking.status === 'confirmed') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancel(booking)}
                          >
                            <X className="h-4 w-4 text-orange-600" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {data.total_pages > 1 && (
                <div className="px-4 py-3 border-t flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Seite {data.page} von {data.total_pages} • {data.total}{' '}
                    Buchungen gesamt
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      Zurück
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === data.total_pages}
                      onClick={() => setPage(page + 1)}
                    >
                      Weiter
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <BookingDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        booking={editingBooking}
        onSuccess={() => {
          setDialogOpen(false);
          setEditingBooking(null);
          queryClient.invalidateQueries({ queryKey: ['bookings'] });
        }}
      />
    </MainLayout>
  );
}
